type SentryStackFrame = {
  filename?: string;
};

type SentryException = {
  type?: string;
  value?: string;
  stacktrace?: {
    frames?: SentryStackFrame[];
  };
};

export type FilterableSentryEvent = {
  level?: string;
  logentry?: {
    message?: string;
  };
  message?: string;
  exception?: {
    values?: SentryException[];
  };
};

/**
 * AdSense RUM(Real User Monitoring) 측정 모듈만 지목한다.
 * `adsbygoogle.js`의 TagError는 광고 슬롯 구현 오류일 수 있으므로 제외하지 않는다.
 */
export const ADSENSE_RUM_FILE = /\/pagead\/.*\/rum[_a-z0-9.]*\.js$/i;

const THIRD_PARTY_FRAME =
  /(?:clerk|wcslog|(?:^|\/)executors\/200\.js(?:$|[?#]))/i;

const AD_NETWORK_FAILURE =
  /^(?:TypeError: )?(?:Load failed|Failed to fetch|NetworkError when attempting to fetch resource\.?|network error) \((?:[a-z0-9-]+\.)*(?:doubleclick\.net|googlesyndication\.com)\)$/i;

const USER_CANCELLED_REQUEST =
  /^(?:AbortError: )?(?:signal is aborted without reason|Fetch is aborted|The operation was aborted\.?|Aborted)$/i;

const BROWSER_EXTENSION_ERROR =
  /(?:Can't find variable: __firefox__|window\.__firefox__|Failed to connect to MetaMask|window\.ethereum\.selectedAddress)/i;

const MISSING_USER_ID_WARNING = /^Missing userId in \S+/;
const LOGIN_REQUIRED_WARNING = /^(?:Error: )?로그인이 필요합니다\.$/;

export const SENTRY_BROWSER_IGNORED_ERRORS = [
  /Can't find variable: error/,
  /error is not defined/,
  /window\.webkit\.messageHandlers/,
  /Java object is gone/,
  /Unexpected token '\('/,
  /Connection closed/,
  /Blocked a frame with origin .* from accessing a cross-origin frame/,
  /^(?:TypeError: )?Load failed$/,
  /^(?:TypeError: )?Failed to fetch$/,
  /^(?:TypeError: )?NetworkError when attempting to fetch resource\.$/,
  /^(?:TypeError: )?cancelled$/,
  AD_NETWORK_FAILURE,
  USER_CANCELLED_REQUEST,
  BROWSER_EXTENSION_ERROR,
  /An unexpected response was received from the server/,
  /Hydration failed/,
  /There was an error while hydrating/,
  /Text content does not match/,
  /did not match/,
  /Failed to read the 'localStorage' property from 'Window'/,
];

export const SENTRY_BROWSER_DENY_URLS = [
  /@clerk\/clerk-js/i,
  /wcslog\.js/i,
  /^(?:chrome|moz)-extension:\/\//i,
  /\/executors\/200\.js(?:$|[?#])/i,
  ADSENSE_RUM_FILE,
];

function getEventMessages(event: FilterableSentryEvent): string[] {
  const messages = [event.message, event.logentry?.message];

  for (const exception of event.exception?.values ?? []) {
    messages.push(exception.value);
    if (exception.type && exception.value) {
      messages.push(`${exception.type}: ${exception.value}`);
    }
  }

  return messages.filter((message): message is string => Boolean(message));
}

function hasThirdPartyFrame(event: FilterableSentryEvent): boolean {
  return (event.exception?.values ?? []).some((exception) =>
    (exception.stacktrace?.frames ?? []).some((frame) => {
      const filename = frame.filename ?? "";
      return THIRD_PARTY_FRAME.test(filename) || ADSENSE_RUM_FILE.test(filename);
    }),
  );
}

/**
 * SDK의 `ignoreErrors`가 메시지 가공 방식에 따라 놓칠 수 있는 이벤트를 한 번 더 판정한다.
 * 예상 가능한 인증 누락은 warning일 때만 버려 실제 인증 예외를 숨기지 않는다.
 */
function isExpectedAuthWarning(
  event: FilterableSentryEvent,
  messages = getEventMessages(event),
): boolean {
  return (
    event.level === "warning" &&
    messages.some(
      (message) =>
        MISSING_USER_ID_WARNING.test(message) ||
        LOGIN_REQUIRED_WARNING.test(message),
    )
  );
}

export function filterSentryBrowserEvent<T extends FilterableSentryEvent>(
  event: T,
): T | null {
  if (hasThirdPartyFrame(event)) {
    return null;
  }

  const messages = getEventMessages(event);

  if (
    messages.some((message) =>
      SENTRY_BROWSER_IGNORED_ERRORS.some((pattern) => pattern.test(message)),
    )
  ) {
    return null;
  }

  if (isExpectedAuthWarning(event, messages)) {
    return null;
  }

  return event;
}

/** 서버와 Edge에서는 브라우저 전용 오류를 걸러내지 않고 예상 인증 warning만 제외한다. */
export function filterSentryServerEvent<T extends FilterableSentryEvent>(
  event: T,
): T | null {
  return isExpectedAuthWarning(event) ? null : event;
}
